<style lang="scss">
@import "./styles/add-contact-popover.scss";
</style>

<template>
  <v-menu
    class="add-contact-popover"
    :close-on-content-click="false"
    v-model="isOpened"
    absolute
    right
    top
    :disabled="isBtnDisabled"
  >
    <v-btn class="add-contact-popover-button" slot="activator" color="primary" round outline :disabled="isBtnDisabled">
      <SleekText xs="sleek-body-5">{{ text }}</SleekText>
    </v-btn>
    <v-card class="add-contact-popover-card old-ui">
      <v-form ref="form" v-model="valid" lazy-validation>
        <v-layout row wrap>
          <v-flex v-if="shareholder" xs12>
            <v-select
              v-model="contactType"
              :items="contactTypes"
              :rules="contactTypeRules"
              label="Individual or Corporate shareholder"
              required
            ></v-select>
          </v-flex>
          <v-flex v-if="standardUser" xs12>
            <v-select
              v-model="contactType"
              :items="[this.$t('$company.organization.standard_user')]"
              :rules="contactTypeRules"
              :label="this.$t('$company.organization.standard_user')"
              required
            ></v-select>
          </v-flex>
          <v-flex v-if="isCorporateShareholder" xs12>
            <v-flex xs12>
              <v-text-field v-model="companyName" :rules="companyNameRules" label="Company Name"></v-text-field>
            </v-flex>
            <SleekText xs="sleek-body-4">Represented by</SleekText>
          </v-flex>
          <v-flex xs12 v-if="!standardUser">
            <v-select
              v-model="existingContactOption"
              :items="existingContactsOptions"
              :rules="existingContactOptionRules"
              label="Pick an existing contact"
              @change="onExistingContactChange"
              clearable
            ></v-select>
          </v-flex>
          <v-flex class="input-offset-right" xs12 sm6>
            <v-text-field
              @keyup="onKeyup"
              v-model="firstName"
              :rules="firstNameRules"
              :label="this.$t('$company.organization.first_name')"
              required
            ></v-text-field>
          </v-flex>
          <v-flex class="input-offset-left" xs12 sm6>
            <v-text-field
              @keyup="onKeyup"
              v-model="lastName"
              :rules="lastNameRules"
              :label="this.$t('$company.organization.last_name')"
              required
            ></v-text-field>
          </v-flex>
          <v-flex xs12>
            <v-text-field
              @keyup="onKeyup"
              v-model="email"
              :rules="emailRules"
              :label="this.$t('$company.organization.email')"
              required
            ></v-text-field>
          </v-flex>
          <v-flex v-if="showMainPointOfContact" xs12>
            <v-checkbox
              v-model="isPointOfContact"
              class="point-of-contact-checkbox"
              color="primary"
              label="Use as main signee for incorporation documents"
            ></v-checkbox>
          </v-flex>
        </v-layout>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" :disabled="!valid" @click="submit">{{ this.$t("$company.organization.add") }}</v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-menu>
</template>

<script>
import { get } from "lodash";
// Maps
// Components
import SleekText from "@shared-components/presentational/SleekText.vue";
// Utilities
import TextUtil from "@/utilities/text-util";
import GlobalTopic from "@/utilities/global-topic";
// Mixins
import { ValidateMixin } from "@/mixins/validate.js";
// Constants
const ENTER_KEY = "Enter";
import { Constants } from "customer-sdk/constants";

const EVENTS = Constants.EVENTS;
const CONTACT_TYPES = Constants.CONTACT_TYPES;
const ON_BOARDING_CONTACTS_EVENTS = Constants.ON_BOARDING_CONTACTS_EVENTS;

//#region Private Methods
let renderSnackbarMessage = (component, message, timeout) => {
  GlobalTopic.publishChange({
    type: EVENTS.ON_OPEN_SNACKBAR,
    data: {
      snackbarText: message,
      timeout: timeout
    }
  });
};

let publishContactAdded = component => {
  let {
    contactsTopic,
    id,
    contactType,
    companyName,
    firstName,
    lastName,
    email,
    isOwner,
    onBoardingTopic,
    showInvitationSend,
    isPointOfContact,
    contactsLimit,
    groupContacts
  } = component;

  let partner = get(component.defaultCorpsecData, "partner", null);

  let textUtil = new TextUtil();
  firstName = firstName ? textUtil.formatToTitleCase(firstName) : firstName;
  lastName = lastName ? lastName.toUpperCase() : lastName;

  email = email.replace(/\s/g, "");
  if (contactsLimit && groupContacts.length >= contactsLimit) {
    renderSnackbarMessage(component, "Max number of shareholders reached. Please remove to add again.", 6000);
  } else {
    if (contactsTopic) {
      component.isOpened = false;
      contactsTopic.publish({
        type: ON_BOARDING_CONTACTS_EVENTS.ON_CONTACT_ADDED,
        contactCategory: component.contactCategory,
        showInvitationSend: showInvitationSend,
        data: { id, contactType, companyName, firstName, lastName, email, isOwner, isPointOfContact, partner },
        onBoardingTopic: onBoardingTopic
      });
    }
  }
};

let resetForm = component => {
  component.$refs.form.reset();
  setTimeout(() => {
    component.id = "";
    component.contactType = component.standardUser
      ? CONTACT_TYPES.STANDARD_USER
      : component.director
      ? CONTACT_TYPES.NOMINEE_DIRECTOR
      : CONTACT_TYPES.INDIVIDUAL_SHAREHOLDER;
  });
};
//#endregion

export default {
  name: "AddContactPopover",
  mixins: [ValidateMixin],
  components: {
    SleekText
  },
  props: {
    text: String,
    contactCategory: String,
    contactsTopic: Object,
    existingContacts: Array,
    director: Boolean,
    shareholder: Boolean,
    standardUser: Boolean,
    showInvitationSend: Boolean,
    onBoardingTopic: Object,
    showMainPointOfContact: Boolean,
    contactsLimit: Number,
    groupContacts: Array,
    defaultCorpsecData: {
      type: Object,
      default: () => {}
    }
  },
  data: function() {
    return {
      isOpened: false,
      valid: true,
      existingContactOption: null,
      existingContactOptionRules: [],
      contactType: this.standardUser
        ? this.$t("$company.organization.standard_user")
        : this.director
        ? CONTACT_TYPES.NOMINEE_DIRECTOR
        : CONTACT_TYPES.INDIVIDUAL_SHAREHOLDER,
      contactTypes: [CONTACT_TYPES.INDIVIDUAL_SHAREHOLDER, CONTACT_TYPES.CORPORATE_SHAREHOLDER],
      contactTypeRules: [value => !!value || this.$t("$company.organization.required_field")],
      id: "",
      companyName: "",
      companyNameRules: [value => !!value || this.$t("$company.organization.required_field")],
      firstName: "",
      firstNameRules: [value => !!value || this.$t("$company.organization.required_first_name")],
      lastName: "",
      lastNameRules: [value => !!value || this.$t("$company.organization.required_last_name")],
      email: "",
      emailRules: [
        value => !!value || this.$t("$company.organization.required_email"),
        value => this.validateEmail(value) || this.$t("$company.organization.invalid_email")
      ],
      isOwner: this.isOwner,
      isPointOfContact: false
    };
  },
  computed: {
    isCorporateShareholder: function() {
      return this.contactType === this.$t("$contact_types.CORPORATE_SHAREHOLDER");
    },
    existingContactsOptions: function() {
      const res = (this.existingContacts || []).map(existingContact => {
        return `${existingContact.firstName} ${existingContact.lastName}`;
      });
      return res;
    },
    isBtnDisabled() {
      if (this.contactsLimit && this.groupContacts.length >= this.contactsLimit) {
        return true;
      } else {
        return false;
      }
    }
  },
  methods: {
    submit() {
      let component = this;

      if (this.$refs.form.validate()) {
        publishContactAdded(component);
        resetForm(component);
      }
    },
    clear() {
      let component = this;

      resetForm(component);
    },
    onKeyup(e) {
      let component = this;

      if (e.key === ENTER_KEY) {
        component.submit();
      }
    },
    onExistingContactChange(value) {
      let foundContact = this.existingContacts.find(existingContact => {
        return `${existingContact.firstName} ${existingContact.lastName}` === value;
      });

      if (foundContact) {
        this.id = foundContact.id;
        this.firstName = foundContact.firstName;
        this.lastName = foundContact.lastName;
        this.email = foundContact.email;
        this.isOwner = foundContact.isOwner;
      } else {
        this.id = "";
        this.firstName = "";
        this.lastName = "";
        this.email = "";
        this.isOwner = null;
      }
    }
  }
};
</script>
